
import { defineComponent, PropType, computed, ref, reactive } from "vue";
import GameSelectWithPicker from "@/components/form/parts/fields/pickers/GameSelectWithPicker.vue";
import SelectedOrderChips from "@/components/form/parts/fields/SelectedOrderChips.vue";
import DateInput from "./DateInput.vue";
import ApiService from "@/core/services/ApiService";
export default defineComponent({
  name: "NestedArrayInput",
  components: { GameSelectWithPicker, SelectedOrderChips, DateInput },
  props: {
    input: { type: Object as PropType<any>, required: true },
    relationships: { type: Object as PropType<Record<string, any[]>>, required: false },
    modelValue: { type: Array as PropType<any[]>, default: () => [] },
  },
  emits: ["update:modelValue", "add", "remove"],
  setup(props, { emit }) {
    const model = computed({
      get: () => props.modelValue,
      set: (v) => emit("update:modelValue", v),
    });

    // Collapse state
    const isCollapsible = computed(() => !!props.input.collapsible);
    const collapsedSet = ref<Set<number>>(new Set());

    // Start all existing items as collapsed
    const ensureCollapsed = () => {
      if (isCollapsible.value && model.value) {
        model.value.forEach((_, idx) => {
          collapsedSet.value.add(idx);
        });
      }
    };
    ensureCollapsed();

    const isCollapsed = (idx: number) => collapsedSet.value.has(idx);
    const toggle = (idx: number) => {
      const next = new Set(collapsedSet.value);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      collapsedSet.value = next;
    };

    const getSummary = (row: Record<string, any>) => {
      if (typeof props.input.summaryFn === 'function') {
        return props.input.summaryFn(row, props.relationships);
      }
      return '';
    };

    const getImagePreview = (row: Record<string, any>) => {
      const imageCol = (props.input.columns || []).find((c: any) => c.type === 'image');
      if (!imageCol) return '';
      return getPreview(row, imageCol);
    };

    const getValue = (row: Record<string, any>, col: any) => {
      const key = col.valueAttr || col.name;
      const val = row?.[key];
      if (col.value === "multiple" && !Array.isArray(val)) return [];
      return val;
    };

    const setValue = (idx: number, col: any, val: any) => {
      const key = col.valueAttr || col.name;
      const next = [...model.value];
      next[idx] = { ...(next[idx] || {}), [key]: val };
      if (typeof col.onChangeCallback === 'function') {
        col.onChangeCallback(next[idx], val, props.relationships);
      }
      model.value = next;
    };

    // Append a value to a multi-select column's array, then reset the dropdown
    const onMultiAdd = (idx: number, col: any, e: Event) => {
      const target = e.target as HTMLSelectElement;
      const raw = target.value;
      if (raw === '' || raw == null) return;
      const numeric = Number(raw);
      const value: any = (raw !== '' && !Number.isNaN(numeric)) ? numeric : raw;
      const key = col.valueAttr || col.name;
      const currentRow = model.value[idx] || {};
      const current: any[] = Array.isArray(currentRow[key]) ? currentRow[key] : [];
      if (!current.includes(value)) {
        setValue(idx, col, [...current, value]);
      }
      target.value = '';
    };

    // Remove a value from a multi-select column's array
    const removeMultiValue = (idx: number, col: any, val: any) => {
      const key = col.valueAttr || col.name;
      const currentRow = model.value[idx] || {};
      const current: any[] = Array.isArray(currentRow[key]) ? currentRow[key] : [];
      setValue(idx, col, current.filter((v) => v !== val));
    };

    const getOptions = (col: any) => {
      if (typeof col.customOptions === "function") return col.customOptions();
      if (Array.isArray(col.options)) return col.options;
      const relKey = col.relationshipName || col.relationshipKey || col.name;
      if (props.relationships && relKey && props.relationships[relKey]) return props.relationships[relKey];
      return [];
    };

    const getOptionLabel = (col: any, value: any) => {
      const opts = getOptions(col);
      const found = opts.find((opt: any) => (opt?.id ?? opt?.value ?? opt) === value);
      return found?.label ?? found?.name ?? value ?? "";
    };

    const move = (from: number, to: number) => {
      if (to < 0 || to >= model.value.length) return;
      const next = [...model.value];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      model.value = next;

      // Update collapsed state for moved items
      const nextCollapsed = new Set<number>();
      collapsedSet.value.forEach((idx) => {
        if (idx === from) {
          nextCollapsed.add(to);
        } else if (from < to && idx > from && idx <= to) {
          nextCollapsed.add(idx - 1);
        } else if (from > to && idx >= to && idx < from) {
          nextCollapsed.add(idx + 1);
        } else {
          nextCollapsed.add(idx);
        }
      });
      collapsedSet.value = nextCollapsed;
    };

    const getPreview = (row: Record<string, any>, col: any) => {
      const val = getValue(row, col);
      if (!val) return "";
      if (val instanceof File) return URL.createObjectURL(val);
      return col.transform ? col.transform(val) : val;
    };

    const onFileChange = (e: Event, idx: number, col: any) => {
      const target = e.target as HTMLInputElement;
      if (target?.files && target.files[0]) {
        setValue(idx, col, target.files[0]);
      }
    };

    // AI image generation
    const generating = reactive<Record<number, boolean>>({});

    const generateImage = async (idx: number, row: Record<string, any>, col: any) => {
      const action = col.generateAction;
      if (!action) return;

      const param = action.getParam(row, props.relationships);
      if (!param) return;

      generating[idx] = true;
      try {
        const { data } = await ApiService.post(`${action.endpoint}/${param}`, {} as any);
        const imageUrl = data?.data?.[action.resultKey] || data?.[action.resultKey];
        if (imageUrl) {
          setValue(idx, col, imageUrl);
        }
      } catch (err) {
        console.error("Failed to generate image:", err);
      } finally {
        generating[idx] = false;
      }
    };

    return {
      model, getValue, setValue, onMultiAdd, removeMultiValue, getOptions, getPreview, onFileChange, getOptionLabel, move,
      isCollapsible, isCollapsed, toggle, getSummary, getImagePreview,
      generating, generateImage,
    };
  },
});
